import { isComplexFilterArr } from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { IFieldInfoProperties, IGetValueArgs } from "@components/smart/FieldInfo";
import { IDependentFieldDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import { IAffectedRow, TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import { IFieldInfo } from "@odata/FieldInfo.utils";
import {
    BankAccountEntity,
    IBankAccountEntity,
    IBankStatementEntity,
    IBusinessPartnerEntity,
    ICompanyBankAccountEntity,
    ILoanAccountEntity
} from "@odata/GeneratedEntityTypes";
import { CompanyBankAccountTypeCode, CountryCode } from "@odata/GeneratedEnums";
import { ICreateFilterStringSettings, IFormatOptions } from "@odata/OData.utils";
import {
    buildComplexBankAccountFilterFromBankAccountString,
    changeCountryByIBAN,
    isBankAccount,
    removeInactiveAccounts,
    setBankValue,
    tryToCreateIBAN
} from "@pages/banks/bankAccounts/BankAccounts.utils";
import { getAgendaCountryCode } from "@pages/companies/Company.shared.utils";
import Iban from "iban";

import { REST_API_URL } from "../constants";
import { BankAccountType, BasicInputSizes, FieldType, GroupedField, Status } from "../enums";
import { TRecordAny, TValue } from "../global.types";
import { IFilterQuery, TableStorage } from "../model/TableStorage";
import BindingContext from "../odata/BindingContext";
import { FormStorage } from "../views/formView/FormStorage";
import { IChangedFilter } from "../views/table/TableView.utils";
import customFetch, { getDefaultPostParams } from "./customFetch";
import { isNotDefined } from "./general";
import { removeWhiteSpace, trimLeadingZeroes } from "./string";

const BANK_ABA_COUNTRIES = ["US"];

export interface IHandleBlur {
    storage: FormStorage;
    bindingContext: BindingContext;
    root: BindingContext;
}

export const createIBANFromAccount = (accountNumber: string, bankCode: string, country: string): string => {
    if (isNotDefined(accountNumber) || isNotDefined(bankCode)) {
        return "";
    }

    const split = accountNumber?.split("-");
    const pre = split.length > 1 ? split[0].padStart(6, "0") : "000000";
    const number = split[1] ?? split[0];
    const formatted = `${bankCode}${pre}${number.padStart(10, "0")}`;

    try {
        return Iban.fromBBAN(country, formatted);
    } catch (e) {
        return "";
    }
};

export const createAccountFromIBAN = (IBAN: string): { bank: string, account: string } => {
    try {
        const acc = Iban.toBBAN(IBAN, "/");

        const [bankCode, prefix, accountNumber] = acc.split("/");

        // remove empty 000 in the start of account
        const trimmed = parseInt(prefix, 10);
        const account = trimmed ? `${trimmed}-${accountNumber}` : accountNumber;

        return {
            bank: bankCode,
            account: account
        };
    } catch (e) {
        return undefined;
    }
};

export const generalBankAccountFormatter = (val: TValue, args?: IFormatOptions): string => {
    const account: ICompanyBankAccountEntity = (args.item?.BankAccount ?? args.item ?? args.entity?.BankAccount) as IBankAccountEntity;
    const ifEmptyValue = val?.toString() || args.placeholder;

    if (account) {
        if (Array.isArray(account.AccountNumber)) {
            return val?.toString();
        }

        const type = getTypeFromAccount(account);
        return formatBankAccount(account, type) || ifEmptyValue;
    }

    return ifEmptyValue;
};

export const getBankAccountFilterDefinition = (prefix: string, includeAccountNumber?: boolean): IFieldInfo["filter"] => {
    const groupByProperties = [`${prefix}/BankCode`, `${prefix}/CountryCode`, `${prefix}/AbaNumber`, `${prefix}/PaymentServiceID`, `${prefix}/IBAN`];

    if (includeAccountNumber) {
        groupByProperties.push(`${prefix}/AccountNumber`);
    }

    return {
        groupByProperties,
        buildFilter: (item: IChangedFilter, settings: ICreateFilterStringSettings, storage: TableStorage): IFilterQuery | string => {
            if (isComplexFilterArr(item.value)) {
                return buildComplexBankAccountFilterFromBankAccountString(item, storage, settings);
            } else {
                // call original function that build filter automatically
                return storage.createFilterQueryRecursive([{
                    ...item,
                    // we need the bc to always be set to AccountNumber, but this function is used from Account navigation filter also
                    bindingContext: includeAccountNumber ? item.bindingContext.navigate("AccountNumber") : item.bindingContext,
                    info: {
                        ...item.info,
                        filter: {
                            ...item.info.filter,
                            // but prevent it from calling this callback again
                            buildFilter: null
                        }
                    }
                }], settings.rootBindingContext);
            }
        }
    };
};

export const getTypeFromAccount = (account: IBankAccountEntity | ILoanAccountEntity): BankAccountType => {
    if (!account) {
        return BankAccountType.Account;
    }

    // some accounts are loaded with Country as object with Code string
    const country = account.CountryCode ?? account?.Country?.Code;

    if ((account as ICompanyBankAccountEntity).CompanyBankAccountTypeCode === CompanyBankAccountTypeCode.PaymentService) {
        return BankAccountType.Service;
    }

    if (isAbaCountry(country)) {
        return BankAccountType.ABA;
    }

    if (hasAccountNumber(country)) {
        return BankAccountType.Account;
    }

    return BankAccountType.IBANOnly;
};

export const formatBankAccountWithoutType = (acc: IBankAccountEntity): string => {
    if (acc) {
        const accountNumber = acc.AccountNumber?.replace(/^0+-/, "");

        if (acc.BankCode && acc.AccountNumber) {
            return `${accountNumber}/${acc.BankCode}`;
        }
        if (acc.AbaNumber && acc.AccountNumber) {
            return `${acc.AbaNumber}/${accountNumber}`;
        }

        return acc.IBAN ?? "";
    }

    return "";
};

export const formatBankAccount = (bankAccount: ICompanyBankAccountEntity | ILoanAccountEntity, type: BankAccountType, usableWithoutBank = false): string => {
    if (!bankAccount) {
        return "";
    }

    if (bankAccount.PaymentServiceID) {
        return bankAccount.PaymentServiceID;
    }

    let accountNumber = bankAccount.AccountNumber;
    accountNumber = accountNumber?.replace(/^0+-/, "");

    switch (type) {
        case BankAccountType.ABA:
            return `${bankAccount.AbaNumber}/${accountNumber}`;

        case BankAccountType.Account:
            if (accountNumber && bankAccount.BankCode) {
                return `${accountNumber}/${bankAccount.BankCode}`;
            } else if (usableWithoutBank) {
                return accountNumber;
            }

            return "";
    }

    return ibanFormatter(bankAccount.IBAN);
};

export interface IAllBankAccount extends IBankAccountEntity {
    BusinessPartner: IBusinessPartnerEntity;
}

interface BankAccountsItemsArgs {
    bankAccounts: ICompanyBankAccountEntity[] | ILoanAccountEntity[];
    hasBusinessPartnerCol?: boolean;
    allowedTypes?: BankAccountType[];
    allowedCompanyBankAccountTypes?: CompanyBankAccountTypeCode[];
    addCurrency?: boolean;
    useValueAsId?: boolean;
}

export const createBankAccountsItems = (args: BankAccountsItemsArgs): ISelectItem[] => {
    const groups: Record<BankAccountType, ISelectItem[]> = {
        [BankAccountType.Account]: [],
        [BankAccountType.IBANOnly]: [],
        [BankAccountType.ABA]: [],
        [BankAccountType.Service]: []
    };

    for (const bankAccount of args.bankAccounts || []) {
        const id = bankAccount.Id ?? (bankAccount as TRecordAny).Code ?? `#${(bankAccount as TRecordAny)[BindingContext.NEW_ENTITY_ID_PROP]}`;
        const type = getTypeFromAccount(bankAccount);

        if (args.allowedTypes && !args.allowedTypes.includes(type)) {
            continue;
        }

        if (args.allowedCompanyBankAccountTypes && !args.allowedCompanyBankAccountTypes.includes(bankAccount.CompanyBankAccountTypeCode)) {
            continue;
        }

        const item: ISelectItem = {
            id: args.useValueAsId ? formatBankAccount(bankAccount, type) : id,
            label: formatBankAccount(bankAccount, type),
            groupId: type,
            additionalData: bankAccount
        };

        if (bankAccount.Name) {
            item.tabularData = [item.label];
            if (args.addCurrency) {
                item.tabularData.push(item.additionalData?.TransactionCurrency?.Code || "");
            }

            item.tabularData.push(bankAccount.Name);
        }

        if (args.hasBusinessPartnerCol) {
            item.tabularData = [item.label, (bankAccount as IAllBankAccount).BusinessPartner?.Name];
        }


        groups[type].push(item);
    }

    return [...groups.account, ...groups.ibanOnly, ...groups.aba, ...groups.service];
};

export const ibanFormatter = (val: TValue): string => {
    let iban = val as string;
    if (Iban.isValid(iban)) {
        iban = removeWhiteSpace(iban);

        const newStringArr: string[] = [];
        const SPACING = 4;
        for (let i = 0, len = iban.length; i < len; i += SPACING) {
            newStringArr.push(iban.slice(i, i + SPACING));
        }

        return newStringArr.join(" ").toUpperCase();
    }

    return iban ?? "";
};

export const getBc = (args: IHandleBlur, name: string): BindingContext => {
    return args.root ? args.root.navigate(name) : args.storage.data.bindingContext.navigate(name);
};

export const handleBankAccountOrIBANBlur = (args: IHandleBlur): boolean => {
    const path = args.bindingContext.getPath(true);

    if (isBankAccount(args.root)) {
        if (path === "IBAN") {
            handleIBANBlur(args);
        }

        if (path === "AccountNumber" || path === "Bank") {
            return handleBankAccountBlur(args);
        }
    }

    return false;
};

export const handleBankAccountOrIBANBlurWithAccountGeneration = (args: IHandleBlur): void => {
    if (isBankAccount(args.root)) {
        const { storage, bindingContext: bc } = args;
        const parent = bc.getParent();
        if (parent.isValidNavigation(BankAccountEntity.Country)) {
            setDefaultCountry(storage, parent.navigate(BankAccountEntity.Country));
        }

        handleBankAccountOrIBANBlur(args);

        const path = args.bindingContext.getPath(true);
        if (path === "IBAN") {
            const val = args.storage.getValue(args.bindingContext);
            const isIBAN = Iban.isValid(val as string);
            if (isIBAN) {
                const { bank, account } = createAccountFromIBAN(val);
                storage.setValue(parent.navigate(BankAccountEntity.AccountNumber), account);
                setBankValue(storage, parent, bank);
            }
        }
    }
};

const setDefaultCountry = (storage: FormStorage, countryBc: BindingContext) => {
    const country = storage.getValue(countryBc, { useDirectValue: false });
    if (!country) {
        const defaultCountry = getAgendaCountryCode(storage.context, null);
        if (defaultCountry === CountryCode.CzechRepublic || defaultCountry === CountryCode.Slovakia) {
            storage.setValue(countryBc, defaultCountry);
        }
    }
};

export const handleIBANBlur = (args: IHandleBlur): void => {
    changeCountryByIBAN(args.storage, args.root);
    args.storage.refresh();
};


export const handleBankAccountBlur = (args: IHandleBlur): boolean => {
    const bcAccount = getBc(args, "AccountNumber");
    let account = args.storage.getValue(bcAccount);
    let isSplit = false;

    const splitted = account?.split("/");

    const countryBc = getBc(args, "Country");

    if (splitted && splitted.length === 2) {
        setDefaultCountry(args.storage, countryBc);
        args.storage.setValue(bcAccount, splitted[0]);
        // we have change value -> revalidate
        args.storage.validateFieldSync(bcAccount);
        setBankValue(args.storage, args.root ?? args.storage.data.bindingContext, splitted[1]);
        account = splitted[0];
        isSplit = true;
    }

    if (account?.startsWith("0")) {
        account = trimLeadingZeroes(account);
        args.storage.setValue(bcAccount, account);
        args.storage.validateFieldSync(bcAccount); // revalidate field as value has changed
    }

    // try to create IBAN
    tryToCreateIBAN(args.storage, bcAccount);

    return isSplit;
};

export const getBankCountry = (args: IGetValueArgs): CountryCode => {
    const bc = args.bindingContext.isCollection() ? args.bindingContext : args.bindingContext.getParent();
    const countryBc = bc.navigate(BankAccountEntity.Country);
    return args.storage.getValue(countryBc, { useDirectValue: false });
};

export const isBankCodeVisible = (args: IGetValueArgs): boolean => {
    const country = getBankCountry(args);
    return !country || hasBankCode(country);
};

export const hasBankCode = (countryCode: CountryCode): boolean => {
    return [CountryCode.CzechRepublic].includes(countryCode);
};

export const isBankAccountVisible = (args: IGetValueArgs): boolean => {
    const countryCode = getBankCountry(args);
    return isNotDefined(countryCode) || hasAccountNumber(countryCode);
};

export const isBankAbaVisible = (args: IGetValueArgs): boolean => {
    return BANK_ABA_COUNTRIES.includes(getBankCountry(args));
};

export const isIBANVisible = (args: IGetValueArgs): boolean => {
    return !isAbaCountry(getBankCountry(args));
};
export const isAbaCountry = (country: string): boolean => {
    return BANK_ABA_COUNTRIES.includes(country);
};

const hasAccountNumber = (countryCode: CountryCode): boolean => {
    return [CountryCode.CzechRepublic, CountryCode.UnitedStatesOfAmerica].includes(countryCode);
};

export const AccountNumberGroupField = (args: IGetValueArgs): GroupedField => {
    const country = getBankCountry(args);
    return BANK_ABA_COUNTRIES.includes(country) ? GroupedField.MultiEnd : GroupedField.MultiStart;
};

export const AbaGroupField = (args: IGetValueArgs): GroupedField => {
    const country = getBankCountry(args);
    return BANK_ABA_COUNTRIES.includes(country) ? GroupedField.MultiStart : undefined;
};

export const handleMarkAsUnrelatedConfirm = async (storage: TSmartODataTableStorage): Promise<boolean> => {
    storage.tableAPI.setState({ loaded: false });
    const activeRows = await storage.tableAPI.getAffectedRows();
    const ids = Array.from(activeRows)?.map((row: IAffectedRow) => {
        return row.key;
    }) || [];

    const result = await customFetch(`${REST_API_URL}/CbaProcessing/MarkAsUnrelatedToBusiness`, {
        ...getDefaultPostParams(),
        body: JSON.stringify({
            PaymentDocumentIds: ids.map(id => parseInt(id))
        })
    });

    let shouldReload = false;
    if (!result || result.ok) {
        storage.data.alert = {
            status: Status.Success,
            title: storage.t("Components:Table.UpdateOk"),
            subTitle: storage.t("Banks:CashBasisAccounting.MarkedAsUnrelated"),
            useFade: true
        };
        shouldReload = true;
    }
    storage.tableAPI.setState({ loaded: true });
    return shouldReload;
};

export const accountAdditionalFields = [{
    id: "TransactionCurrency"
}, {
    id: "BankCode"
}, {
    id: "AbaNumber"
}, {
    id: "Country"
}, {
    id: "IBAN"
}];

export const getBankDependentFields = (prefix = "BankAccount"): IDependentFieldDef[] => {
    return [
        {
            from: { id: "BankStatementNumberRangeDefinition" },
            to: { id: `${prefix}/BankStatementNumberRangeDefinition` }
        },
        { from: { id: "TransactionCurrency" }, to: { id: `${prefix}/TransactionCurrency` } },
        { from: { id: "BankCode" }, to: { id: `${prefix}/BankCode` } },
        { from: { id: "AbaNumber" }, to: { id: `${prefix}/AbaNumber` } },
        { from: { id: "Country" }, to: { id: `${prefix}/Country` } },
        { from: { id: "IBAN" }, to: { id: `${prefix}/IBAN` } },
        { from: { id: "Name" }, to: { id: `${prefix}/Name` } },
        { from: { id: "PaymentServiceID" }, to: { id: `${prefix}/PaymentServiceID` } },
        { from: { id: "AccountNumber" }, to: { id: `${prefix}/AccountNumber` } }
    ];
};

export const getCompanyBankAccountNavigationFieldDefinition = (defArgs: Partial<BankAccountsItemsArgs> = {}): IFieldInfoProperties => {
    return {
        additionalProperties: accountAdditionalFields.concat([{ id: "Name" }, { id: "PaymentServiceID" }, { id: "IsActive" }, { id: "Currency/MinorUnit" }]),
        type: FieldType.ComboBox,
        width: BasicInputSizes.XXL,
        fieldSettings: {
            preloadItems: true,
            displayName: "AccountNumber",
            shouldDisplayAdditionalColumns: true,
            itemsFactory: async (args: IGetValueArgs): Promise<ISelectItem[]> => {
                const accounts = (args.storage as FormStorage).context.getCompanyBankAccounts();
                const items = createBankAccountsItems({ bankAccounts: accounts, addCurrency: true, ...defArgs });
                const statement = (args.storage.data.entity as IBankStatementEntity);
                const accountId = statement.BankAccount?.Id;

                return removeInactiveAccounts(items, accountId);
            }
        },
        formatter: generalBankAccountFormatter,
        columns: [{
            id: "AccountNumber",
            formatter: generalBankAccountFormatter,
            additionalProperties: accountAdditionalFields.concat([
                { id: "Name" }, { id: "BankStatementNumberRangeDefinition" },
                { id: "PaymentServiceID" }, { id: "IsActive" }, { id: "CompanyBankAccountTypeCode" }
            ])
        },
            {
                id: "Name"
            }
        ]
    };
};